import React, { useState, useRef, useEffect } from 'react';
import { CloudOutlined, UserOutlined, SafetyCertificateOutlined, TeamOutlined, EyeOutlined } from '@ant-design/icons';
import { workwiseServices } from '../../api';
import { decodeWorkWiseToken } from '../../utils/auth';
import logoText from '../../assets/DasboardIcons/logo-text.png';
import './HeaderAvatar.css';
import MyHeader from '../Header/Header.module.css';
import { useSelector } from 'react-redux';

const HeaderAvatar = ({ isDashboard = false }) => {
  const isAuthenticated = useSelector(state => state.editor.isAuthenticated);
  const userInfo = decodeWorkWiseToken();
  const documentInformation = useSelector(state => state.editor.documentInformation);
  const userInformation = useSelector(state => state.editor.userInformation);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [storageInfo, setStorageInfo] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageError, setStorageError] = useState(null);

  useEffect(() => {
    function handleClick(e) {
      if (open && ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // fetch user storage balance when dropdown opens (only if authenticated)
  useEffect(() => {
    if (!open || !isAuthenticated) return;

    async function fetchStorage() {
      setStorageLoading(true);
      setStorageError(null);
      try {
        const response = await workwiseServices.getUserBalance();
        // expecting response.data.data.storage and response.data.data.allocatedStorage
        setStorageInfo(response.data?.data || null);
      } catch (err) {
        setStorageError(err.message || 'Failed to load storage info');
        setStorageInfo(null);
      } finally {
        setStorageLoading(false);
      }
    }

    fetchStorage();
  }, [open, isAuthenticated]);

  // helper to format bytes into human-readable sizes (GB/MB)
  const formatBytes = (bytes) => {
    if (bytes == null || isNaN(bytes)) return '0 B';
    const abs = Math.abs(Number(bytes));
    if (abs === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(abs) / Math.log(1024));
    const value = abs / Math.pow(1024, i);
    return `${value.toFixed(i >= 3 ? 2 : 1)} ${units[i]}`; // more precision for GB+
  };

  // helper to format bytes specifically to GB
  const formatToGB = (bytes) => {
    if (bytes == null || isNaN(bytes)) return '0.00 GB';
    const gb = Number(bytes) / (1024 ** 3);
    return `${gb.toFixed(3)} GB`;
  };

  const redirectTo = (url) => {
    window.open(url, '_blank');
  };

  const getInitial = () => {
    if (!isAuthenticated || !userInfo?.name) return '?';
    return userInfo.name.charAt(0).toUpperCase();
  };

  function openLoginWindow() {
    const width = 900; // Desired width in pixels
    const height = 600; // Desired height in pixels
    const url = "https://workw.com/login"; // Or any other URL you want to load
    const windowName = "Workwise Authentication";
    const left = (window.screen.width / 2) - (width / 2);
    const top = 50;
    // Define window features
    const features = `width=${width},height=${height},left=${left},top=${top},
                    toolbar=no,menubar=no,scrollbars=no,resizable=yes,
                    location=no,directories=no,status=no`;

    window.open(url, windowName, features);
  }

  const firstName = userInfo?.name?.split(' ')[0] || '';
  const initial = getInitial();

  return (
    <div className="ha-root" ref={ref} style={{ margin: 0, padding: 0 }}>
      <button
        className="ha-btn"
        onClick={() => setOpen(!open)}
        aria-label="User menu"
      >
        {isAuthenticated && userInfo?.image ? (
          <img src={userInfo.image} alt={userInfo.name || 'User'} className="ha-avatar-img" />
        ) : (
          <div className="ha-avatar-initial" style={{ backgroundColor: 'var(--primary-blue)' }}>{initial}</div>
        )}
      </button>
      {open && (
        <div className="ha-dropdown" role="dialog">
          <div className="ha-dropdown-inner">
            {isAuthenticated ? (
              <>
                {/* Email header with close button */}
                <div className="ha-header">
                  <span className="ha-header-email">{userInfo?.email}</span>
                  <button className="ha-close" onClick={() => setOpen(false)} aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>

                {/* Avatar */}
                <div className="ha-avatar-container ha-avatar-large">
                  {userInfo?.image ? (
                    <img src={userInfo.image} alt={userInfo.name || 'User'} className="ha-dropdown-img" />
                  ) : (
                    <div className="ha-dropdown-initial" style={{ backgroundColor: 'var(--primary-blue)' }}>{initial}</div>
                  )}
                </div>

                {/* Name greeting */}
                <div className="ha-name">Hi, {firstName}!</div>

                {/* Manage Workwise Account button */}
                <button className="ha-manage" onClick={() => redirectTo(`https://workw.com/user/${userInfo?.userId}`)}>Manage your Workwise Account</button>

                {/* Add account and Sign out buttons */}
                <div className="ha-actions">
                  <button className="ha-action-btn" onClick={() => redirectTo('https://workw.com/')}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.67">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 3.33v13.34m6.67-6.67H3.33" />
                    </svg>
                    <span>Switch account</span>
                  </button>
                  <div className="ha-actions-divider"></div>
                  <button className="ha-action-btn" onClick={() => redirectTo('https://workw.com/')}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.67">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.17 13.33l3.33-3.33m0 0l-3.33-3.33m3.33 3.33H5.83m5 3.33v.83a2.5 2.5 0 01-2.5 2.5H5a2.5 2.5 0 01-2.5-2.5V5.83A2.5 2.5 0 015 3.33h3.33a2.5 2.5 0 012.5 2.5v.84" />
                    </svg>
                    <span>Sign out</span>
                  </button>
                </div>

                {/* Document rights and role */}
                {documentInformation && !isDashboard && (
                  <div className="ha-doc-info">
                    <span className="ha-doc-item">
                      {/* Use TeamOutlined for collaborator and EyeOutlined for reader */}
                      {documentInformation.rightType == 2 ? <TeamOutlined className="ha-icon" /> : <EyeOutlined className="ha-icon" />}
                      {documentInformation.rightType == 2 ? "Collaborator" : "Reader"}
                    </span>
                    <div className="ha-doc-divider"></div>
                    <span className="ha-doc-item">
                      {documentInformation.createBy == userInformation?.userId ? <SafetyCertificateOutlined className="ha-icon" /> : <UserOutlined className="ha-icon" />}
                      {documentInformation.createBy == userInformation?.userId ? "Creator" : "Member"}
                    </span>
                  </div>
                )}

                {/* Storage usage */}
                <div className="ha-storage">
                  <CloudOutlined className="ha-icon" />
                  {storageLoading ? (
                    <span>Loading...</span>
                  ) : storageError ? (
                    <span>Storage info unavailable</span>
                  ) : storageInfo ? (
                    (() => {
                      const used = Number(storageInfo.storage) || 0;
                      const allocated = Number(storageInfo.allocatedStorage) || 0;
                      return (
                        <span>{formatToGB(used)} used of {formatToGB(allocated)}</span>
                      );
                    })()
                  ) : (
                    <span>—</span>
                  )}
                </div>

                {/* Footer links */}
                <div className="ha-footer">
                  <a href="https://workw.com" target="_blank" rel="noopener noreferrer" className="ha-footer-link">Privacy Policy</a>
                  <span className="ha-footer-dot">•</span>
                  <a href="https://workw.com" target="_blank" rel="noopener noreferrer" className="ha-footer-link">Terms of Service</a>
                </div>
              </>
            ) : (
              <div className="ha-login-prompt">
                <img
                  src={logoText}
                  alt="Workwise"
                />
                <span>Please log in to access your account</span>
                <button className="ha-login-btn" onClick={openLoginWindow}>Workwise Login</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderAvatar;
